<template>
  <div v-if="isVisible" class="jwt-debug-panel">
    <div class="debug-header">
      <h3>🔍 JWT Debug Panel</h3>
      <div class="debug-actions">
        <button @click="refresh" class="btn-refresh" data-testid="jwt-debug-refresh" :title="(t as any)('Refresh')">🔄</button>
        <button @click="toggleMinimize" class="btn-minimize" data-testid="jwt-debug-minimize" :title="(t as any)('Minimize')">{{ isMinimized ? '📖' : '📕' }}</button>
        <button @click="close" class="btn-close" data-testid="jwt-debug-close" :title="(t as any)('Close')">❌</button>
      </div>
    </div>

    <div v-if="!isMinimized" class="debug-content">
      <!-- Access Token -->
      <div class="token-section">
        <h4>Access Token</h4>
        <div v-if="accessTokenData" class="token-data">
          <div class="token-raw">
            <strong>Raw Token (first 50 chars):</strong>
            <code>{{ accessTokenRaw }}</code>
          </div>
          <div class="token-decoded">
            <strong>Decoded Payload:</strong>
            <pre>{{ JSON.stringify(accessTokenData, null, 2) }}</pre>
          </div>
          <div class="token-check">
            <strong>versaoSistema in Access Token:</strong>
            <span :class="accessHasVersao ? 'found' : 'not-found'">
              {{ accessHasVersao ? '✅ Found: ' + accessTokenData.versaoSistema : '❌ Not Found' }}
            </span>
          </div>
        </div>
        <div v-else class="no-token">
          ⚠️ No Access Token Available
        </div>
      </div>

      <!-- ID Token -->
      <div class="token-section">
        <h4>ID Token</h4>
        <div v-if="idTokenData" class="token-data">
          <div class="token-raw">
            <strong>Raw Token (first 50 chars):</strong>
            <code>{{ idTokenRaw }}</code>
          </div>
          <div class="token-decoded">
            <strong>Decoded Payload:</strong>
            <pre>{{ JSON.stringify(idTokenData, null, 2) }}</pre>
          </div>
          <div class="token-check">
            <strong>versaoSistema in ID Token:</strong>
            <span :class="idHasVersao ? 'found' : 'not-found'">
              {{ idHasVersao ? '✅ Found: ' + idTokenData.versaoSistema : '❌ Not Found' }}
            </span>
          </div>
          <div class="token-check">
            <strong>custom:versaoSistema in ID Token:</strong>
            <span :class="idHasCustomVersao ? 'found' : 'not-found'">
              {{ idHasCustomVersao ? '✅ Found: ' + idTokenData['custom:versaoSistema'] : '❌ Not Found' }}
            </span>
          </div>
        </div>
        <div v-else class="no-token">
          ⚠️ No ID Token Available
        </div>
      </div>

      <!-- User Object from Permissions -->
      <div class="token-section">
        <h4>User Object (from permissionsService)</h4>
        <div v-if="userData" class="token-data">
          <div class="token-decoded">
            <pre>{{ JSON.stringify(userData, null, 2) }}</pre>
          </div>
          <div class="token-check">
            <strong>versaoSistema in User Object:</strong>
            <span :class="userHasVersao ? 'found' : 'not-found'">
              {{ userHasVersao ? '✅ Found: ' + userData.versaoSistema : '❌ Not Found' }}
            </span>
          </div>
        </div>
        <div v-else class="no-token">
          ⚠️ No User Data Available
        </div>
      </div>

      <!-- Search All Fields -->
      <div class="token-section search-section">
        <h4>🔎 Search for "versao" in all token fields:</h4>
        <div class="search-results">
          <div v-if="versaoFields.length > 0">
            <div v-for="field in versaoFields" :key="field.path" class="search-result">
              <strong>{{ field.path }}:</strong> <code>{{ field.value }}</code>
            </div>
          </div>
          <div v-else class="not-found">
            ❌ No fields containing "versao" found in any token
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { tokenService } from '../services/tokenService.ts';
import { permissionsService } from '../services/permissionsService.ts';

const { t } = useI18n();

const isVisible = ref(false);
const isMinimized = ref(false);
const accessTokenData = ref<Record<string, any> | null>(null);
const idTokenData = ref<Record<string, any> | null>(null);
const userData = ref<Record<string, any> | null>(null);
const accessTokenRaw = ref('');
const idTokenRaw = ref('');

// Decode JWT helper
function decodeJWT(token: string): Record<string, any> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

// Computed checks
const accessHasVersao = computed(() => {
  return accessTokenData.value && 'versaoSistema' in accessTokenData.value;
});

const idHasVersao = computed(() => {
  return idTokenData.value && 'versaoSistema' in idTokenData.value;
});

const idHasCustomVersao = computed(() => {
  return idTokenData.value && 'custom:versaoSistema' in idTokenData.value;
});

const userHasVersao = computed(() => {
  return userData.value && 'versaoSistema' in userData.value;
});

// Search for any field containing "versao"
const versaoFields = computed((): Array<{ path: string; value: string }> => {
  const results: Array<{ path: string; value: string }> = [];

  function searchObject(obj: Record<string, any>, prefix = ''): void {
    if (!obj || typeof obj !== 'object') return;

    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;

      if (key.toLowerCase().includes('versao')) {
        results.push({ path, value: JSON.stringify(value) });
      }

      if (typeof value === 'object' && value !== null) {
        searchObject(value, path);
      }
    }
  }

  if (accessTokenData.value) {
    searchObject(accessTokenData.value, 'AccessToken');
  }
  if (idTokenData.value) {
    searchObject(idTokenData.value, 'IDToken');
  }
  if (userData.value) {
    searchObject(userData.value, 'UserObject');
  }

  return results;
});

function loadTokens(): void {
  // Get access token
  const accessToken = tokenService.getAccessToken();
  if (accessToken) {
    accessTokenRaw.value = accessToken.substring(0, 50) + '...';
    accessTokenData.value = decodeJWT(accessToken);
  }

  // Get ID token
  const idToken = tokenService.getIdToken();
  if (idToken) {
    idTokenRaw.value = idToken.substring(0, 50) + '...';
    idTokenData.value = decodeJWT(idToken);
  }

  // Get user object
  userData.value = permissionsService.getUser();
}

function refresh(): void {
  loadTokens();
}

function toggleMinimize(): void {
  isMinimized.value = !isMinimized.value;
}

function close(): void {
  isVisible.value = false;
}

onMounted(() => {
  loadTokens();
});
</script>

<style scoped>
.jwt-debug-panel {
  position: fixed;
  top: 80px;
  right: 20px;
  width: 600px;
  max-height: 80vh;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  border: 2px solid #00ff00;
  border-radius: 8px;
  z-index: 999999;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 255, 0, 0.3);
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: rgba(0, 255, 0, 0.1);
  border-bottom: 1px solid #00ff00;
}

.debug-header h3 {
  margin: 0;
  font-size: 16px;
  color: #00ff00;
}

.debug-actions {
  display: flex;
  gap: 8px;
}

.debug-actions button {
  background: transparent;
  border: 1px solid #00ff00;
  color: #00ff00;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.debug-actions button:hover {
  background: rgba(0, 255, 0, 0.2);
}

.debug-content {
  max-height: calc(80vh - 60px);
  overflow-y: auto;
  padding: 15px;
}

.token-section {
  margin-bottom: 20px;
  padding: 10px;
  background: rgba(0, 255, 0, 0.05);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 4px;
}

.token-section h4 {
  margin: 0 0 10px 0;
  color: #00ff00;
  font-size: 14px;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  padding-bottom: 5px;
}

.token-data {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.token-raw code,
.token-decoded pre,
.search-result code {
  display: block;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px;
  border-radius: 4px;
  overflow-x: auto;
  color: #00ff00;
  font-size: 11px;
  margin-top: 5px;
}

.token-decoded pre {
  max-height: 200px;
  overflow-y: auto;
}

.token-check {
  padding: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.token-check strong {
  display: block;
  margin-bottom: 5px;
}

.found {
  color: #00ff00;
  font-weight: bold;
}

.not-found {
  color: #ff9900;
  font-weight: bold;
}

.no-token {
  color: #ff9900;
  padding: 10px;
  text-align: center;
}

.search-section {
  background: rgba(255, 153, 0, 0.1);
  border-color: #ff9900;
}

.search-section h4 {
  color: #ff9900;
  border-color: rgba(255, 153, 0, 0.3);
}

.search-results {
  margin-top: 10px;
}

.search-result {
  padding: 5px;
  margin-bottom: 5px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.search-result strong {
  color: #ff9900;
}

/* Scrollbar styling */
.debug-content::-webkit-scrollbar,
.token-decoded pre::-webkit-scrollbar {
  width: 8px;
}

.debug-content::-webkit-scrollbar-track,
.token-decoded pre::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
}

.debug-content::-webkit-scrollbar-thumb,
.token-decoded pre::-webkit-scrollbar-thumb {
  background: rgba(0, 255, 0, 0.5);
  border-radius: 4px;
}

.debug-content::-webkit-scrollbar-thumb:hover,
.token-decoded pre::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 255, 0, 0.7);
}
</style>
